import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, Container, Paper } from '@mui/material';
import { Block as BlockIcon, Home as HomeIcon } from '@mui/icons-material';
import { useAuth } from '@store/authStore';
import { getDefaultRouteForRole } from '@config/roles';

interface LocationState {
  from?: {
    pathname: string;
  };
  defaultRoute?: string;
}

/**
 * Unauthorized Access Page
 * Shown when a user tries to access a route they don't have permission for
 */
const Unauthorized: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const state = location.state as LocationState;
  const attemptedPath = state?.from?.pathname || 'the requested page';
  const defaultRoute = state?.defaultRoute || getDefaultRouteForRole(user?.role);

  const handleGoHome = () => {
    navigate(defaultRoute, { replace: true });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={12}
          sx={{
            padding: 4,
            textAlign: 'center',
            borderRadius: 3,
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(26, 26, 46, 0.9)'
                : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: 3,
            }}
          >
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
                    : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
              }}
            >
              <BlockIcon sx={{ fontSize: 60, color: 'white' }} />
            </Box>
          </Box>

          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              marginBottom: 2,
              background: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            403
          </Typography>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              marginBottom: 2,
              color: (theme) => theme.palette.text.primary,
            }}
          >
            Access Denied
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: (theme) => theme.palette.text.secondary,
              marginBottom: 1,
            }}
          >
            You don't have permission to access this page.
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: (theme) => theme.palette.text.secondary,
              marginBottom: 3,
              fontStyle: 'italic',
            }}
          >
            Your current role: <strong>{user?.role || 'Unknown'}</strong>
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: (theme) => theme.palette.text.disabled,
              marginBottom: 4,
              display: 'block',
            }}
          >
            Attempted to access: {attemptedPath}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={handleGoHome}
              sx={{
                paddingX: 4,
                paddingY: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                background: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  background: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, #5568d3 0%, #65398b 100%)'
                      : 'linear-gradient(135deg, #5568d3 0%, #65398b 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                },
              }}
            >
              Go to Dashboard
            </Button>

            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                paddingX: 4,
                paddingY: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                borderColor: (theme) =>
                  theme.palette.mode === 'dark' ? '#667eea' : '#764ba2',
                color: (theme) =>
                  theme.palette.mode === 'dark' ? '#667eea' : '#764ba2',
                '&:hover': {
                  borderColor: (theme) =>
                    theme.palette.mode === 'dark' ? '#5568d3' : '#65398b',
                  backgroundColor: 'rgba(102, 126, 234, 0.05)',
                },
              }}
            >
              Go Back
            </Button>
          </Box>

          <Box sx={{ marginTop: 4, paddingTop: 3, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
            <Typography
              variant="body2"
              sx={{
                color: (theme) => theme.palette.text.secondary,
              }}
            >
              If you believe this is an error, please contact your administrator.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Unauthorized;
